import { EFinancingCompany } from '@if/core/types'
import { formatCurrency } from '@if/core/utils'
import { DocumentField, SummaryCard } from '@if/mui'
import { MerchantId } from '@src/types/Constants'
import { FundingSummaryDto } from '@src/types/FundingSummaryDto'
import { Briefcase, Building2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface Props {
  fundingSummary: FundingSummaryDto
}

const isPayCustomer = (merchantId: string) =>
  merchantId === MerchantId.PayCustomerByBankTransfer.toString() ||
  merchantId === MerchantId.PayCustomerByCheque.toString()

const MerchantInformationCard = ({ fundingSummary }: Props) => {
  const { t } = useTranslation()

  // A single pay-the-customer payment means the full amount goes to the client, not the merchant.
  const payments = fundingSummary.merchantPayments
  const isFullAmountPaidToClient = payments.length === 1 && isPayCustomer(payments[0].merchantId)

  if (fundingSummary.financingCompanyId === EFinancingCompany.Iceberg) {
    return (
      <SummaryCard title={t('fundingSummary.merchantInformation')} icon={Building2}>
        <DocumentField label={t('fundingSummary.paymentMethod')} value={t('fundingSummary.directDeposit')} strong />
        <DocumentField label={t('common.merchant')} value={fundingSummary.merchantName} strong />
        <DocumentField label={t('fundingSummary.address')} value={fundingSummary.merchantAddress} strong />
        <DocumentField label={t('fundingSummary.phone')} value={fundingSummary.merchantPhone} strong />
      </SummaryCard>
    )
  }

  return (
    <SummaryCard title={t('fundingSummary.serviceProviderInformation')} icon={Briefcase}>
      <DocumentField
        label={t('fundingSummary.internalAccountNumber')}
        value={fundingSummary.merchantId?.split('*')[1] ?? fundingSummary.merchantId}
        strong
      />
      <DocumentField label={t('fundingSummary.contact')} value={fundingSummary.merchantContact} strong />
      <DocumentField
        label={t('fundingSummary.paymentMethod')}
        value={
          isFullAmountPaidToClient
            ? t('fundingSummary.paymentMethodPayableToClient', { method: fundingSummary.merchantPaymentMethod })
            : fundingSummary.merchantPaymentMethod
        }
        strong
      />
      <DocumentField label={t('fundingSummary.serviceProvider')} value={fundingSummary.merchantName} strong />
      <DocumentField label={t('fundingSummary.address')} value={fundingSummary.merchantAddress} strong />
      {fundingSummary.reducedInterestRate != null && (
        <DocumentField
          label={t('fundingSummary.promotionalRate')}
          value={`${fundingSummary.reducedInterestRate}%`}
          strong
        />
      )}
      {fundingSummary.reducedRateDurationInMonths != null && (
        <DocumentField
          label={t('fundingSummary.promotionalTerm')}
          value={`${fundingSummary.reducedRateDurationInMonths}`}
          strong
        />
      )}
      {fundingSummary.interestRate != null && (
        <DocumentField
          label={t('fundingSummary.rateOnBalanceAfterTerm')}
          value={`${fundingSummary.interestRate}%`}
          strong
        />
      )}
      {fundingSummary.merchantFee && (
        <DocumentField
          label={t('fundingSummary.retainedFee')}
          value={formatCurrency(fundingSummary.merchantFee)}
          strong
        />
      )}
    </SummaryCard>
  )
}

export default MerchantInformationCard
